import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { contacts as contactsApi } from '@repletoryreach/sdk-api';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { normalisePhone } from '@repletoryreach/sdk-utils/phone';
import { Group, Pill, Stack, Text, Button, Loader } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { usePhoneDefaultCountry } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../../i18n/I18nProvider';

/** One chosen recipient. `name` is the contact book's, where they are in it. */
export interface Recipient {
  msisdn: string;
  name?: string | null;
}

export interface RecipientPickerProps {
  value: Recipient[];
  onChange: (next: Recipient[]) => void;
  /** Above this the screen says to use a broadcast instead. */
  max: number;
  disabled?: boolean;
}

/**
 * Pick people one at a time: search the contact book by name or number, or
 * type a number that is not in it yet.
 *
 * The typed number goes through `@repletoryreach/sdk-utils/phone`, the one
 * rule the whole platform reads numbers with, using the organisation's own
 * default country from `PhoneDefaultsProvider`. So somebody types
 * `082 111 0001` and gets `+27821110001`, the form the book and the channel
 * both store, without having to know that.
 */
export function RecipientPicker({ value, onChange, max, disabled }: RecipientPickerProps) {
  const t = useT();
  const [term, setTerm] = useState('');
  const debounced = useDebounce(term, 250);
  const inputRef = useRef<HTMLInputElement>(null);
  const country = usePhoneDefaultCountry();

  const full = value.length >= max;

  const search = useQuery({
    // The book is only searched once there is something to search for: an
    // empty term would return the first fifty contacts, which is a list, not
    // an answer.
    enabled: debounced.trim().length >= 2 && !full,
    queryKey: ['contacts', 'compose-search', debounced],
    queryFn: () => contactsApi.listContacts({ search: debounced.trim(), limit: 8 }),
    staleTime: 15_000
  });

  const chosen = useMemo(
    () => new Set(value.map((r) => r.msisdn.replace(/\D/g, ''))),
    [value]
  );

  /*
   * Who the book found. An email-only contact (4 October 2026) has no number,
   * so WhatsApp cannot reach them: they are listed, so a search for them does
   * not read as "not in the book", but cannot be picked.
   */
  const found = search.data?.data ?? [];
  const matches = found.filter((c): c is typeof c & { msisdn: string } =>
    !!c.msisdn && !chosen.has(c.msisdn.replace(/\D/g, '')));
  const unreachable = found.filter((c) => !c.msisdn);

  /**
   * Whether what has been typed reads as a phone number nobody in the book
   * has. Offering "message +27 82 111 0001" for a term that is plainly a name
   * would be noise, so the rule has to accept it first.
   */
  const typedNumber = useMemo(() => {
    const raw = term.trim();
    if (raw.length < 4 || !/\d/.test(raw)) return null;
    const result = normalisePhone(raw, country ?? null);
    if (!result.ok || !result.e164) return null;
    if (chosen.has(result.e164.replace(/\D/g, ''))) return null;
    return result.e164;
  }, [term, country, chosen]);

  function add(msisdn: string, name?: string | null) {
    if (full) return;
    const digits = msisdn.replace(/\D/g, '');
    if (value.some((r) => r.msisdn.replace(/\D/g, '') === digits)) return;
    onChange([...value, { msisdn, name }]);
    setTerm('');
    inputRef.current?.focus();
  }

  function remove(msisdn: string) {
    onChange(value.filter((r) => r.msisdn !== msisdn));
  }

  return (
    <Stack gap="sm">
      <TextInput
        ref={inputRef}
        label={t('compose.recipients.label')}
        description={t('compose.recipients.hint')}
        placeholder={t('compose.recipients.placeholder')}
        value={term}
        disabled={disabled || full}
        autoComplete="off"
        onChange={(e) => setTerm(e.currentTarget.value)}
        onKeyDown={(e) => {
          // Enter takes the obvious candidate: the typed number if it reads as
          // one, otherwise the first person the book found.
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (typedNumber) add(typedNumber);
          else if (matches.length > 0) add(matches[0].msisdn, matches[0].name);
        }}
        rightSection={search.isFetching ? <Loader size="xs" /> : undefined}
      />

      {/* The people chosen so far, under the control they were chosen with:
          the label names the field and the hint explains it, so nothing goes
          between the two. */}
      {value.length > 0 && (
        <Group gap={6} className="rr-compose__chips">
          {value.map((r) => (
            <Pill
              key={r.msisdn}
              withRemoveButton={!disabled}
              onRemove={() => remove(r.msisdn)}
              removeLabel={t('compose.recipients.remove', { who: r.name ?? r.msisdn })}
            >
              {r.name ? `${r.name} · ${r.msisdn}` : r.msisdn}
            </Pill>
          ))}
        </Group>
      )}

      {/* The way out of the cap, said before somebody hits it a second time. */}
      {full && (
        <Alert color="warning" title={t('compose.recipients.full.title')}>
          {t('compose.recipients.full.body', { max })}
        </Alert>
      )}

      {!full && (typedNumber || matches.length > 0) && (
        <Stack gap={4} className="rr-compose__matches">
          {typedNumber && (
            <button type="button" className="rr-compose__match" onClick={() => add(typedNumber)}>
              <Text role="bodySmall" fw={600}>{typedNumber}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('compose.recipients.notInBook')}</Text>
            </button>
          )}
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              className="rr-compose__match"
              onClick={() => add(c.msisdn, c.name)}
            >
              <Text role="bodySmall" fw={600}>{c.name ?? c.msisdn}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {c.name ? c.msisdn : t('compose.recipients.noName')}
              </Text>
            </button>
          ))}
        </Stack>
      )}

      {!full && unreachable.length > 0 && (
        <Stack gap={4} className="rr-compose__matches">
          {unreachable.map((c) => (
            <div key={c.id} className="rr-compose__match" aria-disabled="true" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
              <Text role="bodySmall" fw={600}>{c.name ?? c.email ?? ''}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {c.name && c.email ? `${c.email} · ` : ''}{t('compose.recipients.noNumber')}
              </Text>
            </div>
          ))}
        </Stack>
      )}

      {!full && debounced.trim().length >= 2 && !search.isFetching && matches.length === 0 && unreachable.length === 0 && !typedNumber && (
        <Text role="caption" c="var(--rr-color-text-muted)">{t('compose.recipients.noMatches')}</Text>
      )}

      {value.length > 0 && !disabled && (
        <Group justify="space-between" align="center">
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('compose.recipients.count', { count: value.length, max })}
          </Text>
          <Button size="xs" variant="subtle" onClick={() => onChange([])}>
            {t('compose.recipients.clear')}
          </Button>
        </Group>
      )}
    </Stack>
  );
}
